/**
 * `status` — `StatusValue` (`specs/M2-tier1-column-types.md` §5.2, `[09§A.7 status]`).
 *
 * **The `index` trap.** `settings.labels[].id` is the immutable label id and
 * `settings.labels[].index` is the display **position**; the cell's `value.index` is the
 * **label id**, not the position. The internal PHP job resolves a cell with
 * `labels[value.index]` and writes back `{"index": <label id>}` `[php§3.2]`. Filters,
 * automations and `create_labels_if_missing` all use that same id `[cvs§4.7]`.
 *
 * **Two serialisations, one store.** The canonical settings are the decoded `settings_str`
 * `[php§4]`: `labels` is an object keyed by label id, ordering comes from `labels_positions_v2`
 * (independent of id order), `done_colors` is the list of label ids that count as done, and
 * `color_mapping` remaps stored colour indexes. `Column.settings` — the array form — is derived
 * from all of that by `toMondaySettings` and is never stored.
 *
 * `create_labels_if_missing` is deliberately NOT handled here: creating a label mutates the
 * board structure, which is a service-layer concern (permissions + activity + events, B5 §5.7).
 */
import { z } from 'zod';
import { defaultSummarySql, sqlFragment } from '../sql.js';
import { textShadow } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
import { statusFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
/** *"Max 40 labels per column"* `[09§A.7 status]`. */
export const MAX_STATUS_LABELS = 40;
export const statusLabelColorSchema = z.looseObject({
    /** Live hex, e.g. `#fdab3d`. */
    color: z.string(),
    border: z.string(),
    /**
     * A **fourth** colour vocabulary, hyphenated and *not* the `StatusColumnColors` enum
     * (`grass-green`, `red-shadow`, `bright-blue`) `[php§4.6]`. Stored verbatim, never derived.
     */
    var_name: z.string().optional(),
});
/** Canonical settings = the decoded `settings_str` `[php§4]`. */
export const statusSettingsSchema = z.looseObject({
    /** label id -> label text. Ids are sparse and immutable; never renumber `[php§4.2]`. */
    labels: z.record(z.string(), z.string()),
    /** label id -> display position, independent of id order `[php§4.3]`. */
    labels_positions_v2: z.record(z.string(), z.number()).optional(),
    labels_colors: z.record(z.string(), statusLabelColorSchema).optional(),
    labels_descriptions: z.record(z.string(), z.string()).optional(),
    /** Label **ids** that count as done; `settings.labels[].is_done` flattens it `[php§4.4]`. */
    done_colors: z.array(z.number()).optional(),
    /** Remaps stored colour indexes to palette indexes for recoloured labels `[php§4.5]`. */
    color_mapping: z.record(z.string(), z.number()).optional(),
    deactivated_labels: z.array(z.number()).optional(),
    /** UI-only, present in `settings_str` and absent from `settings` — round-trip it `[php§4.7]`. */
    hide_footer: z.boolean().optional(),
});
/**
 * Six shapes occur across the 2,844 populated cells sampled `[cvs§2 status]`; only `index` is
 * guaranteed, so the schema is loose and every extra key round-trips.
 */
export const statusValueSchema = z.looseObject({
    index: z.number().int(),
    /*
     * P-298 · the sample missed two shapes the import carries: `post_id` as a **number** (362
     * cells) and `changed_at: null` (2 cells). Both used to fail the parse, and a failed parse reads
     * as "no status", so 32 tasks whose cell said Done stayed open in Tasks and could not be
     * marked done (writing Done to a Done cell changes nothing).
     */
    post_id: z.union([z.string(), z.number()]).nullable().optional(),
    changed_at: z.string().nullable().optional(),
    /** monday's own marker for a broken label reference (21 cells) `[cvs§2 status]`. */
    invalid: z.boolean().optional(),
    textual_value: z.string().optional(),
});
/**
 * monday's out-of-the-box labels. Confirmed against a column created through
 * `create_status_column` on the sandbox board: ids 0/1/2 with `done_colors: []`
 * (`0213-tier-b-columns-settings-and-settings-str.json`). Open question Q55 asks what a brand
 * new *UI*-created column ships with.
 */
export const DEFAULT_STATUS_SETTINGS = {
    labels: { '0': 'Working on it', '1': 'Done', '2': 'Stuck' },
    labels_positions_v2: { '0': 0, '1': 1, '2': 2 },
    labels_colors: {
        '0': { color: '#fdab3d', border: '#e99729', var_name: 'orange' },
        '1': { color: '#00c875', border: '#00b461', var_name: 'green-shadow' },
        '2': { color: '#df2f4a', border: '#ce3048', var_name: 'red-shadow' },
    },
    done_colors: [],
    color_mapping: {},
};
/* ── label lookups ────────────────────────────────────────────────────────── */
export function labelIds(settings) {
    return Object.keys(settings.labels).map(Number);
}
/**
 * The label text for a **label id** (`value.index`), or undefined `[php§3.2]`.
 *
 * The `typeof` guard is a seatbelt, not a shape: `settings.labels` is `Record<string, string>`
 * by schema, but a column whose settings never went through `fromMondaySettings` carries
 * monday's *other* serialisation — an array of `{id, color, index, label, is_done}` — and
 * `labels["0"]` is then the label **object**. Round 6b: rendering that object as a React child
 * crashed the whole board route with React error #31. The normaliser
 * (`normaliseColumnSettings`) is the fix; this makes the one function every renderer, picker,
 * bucket and filter reads the text through incapable of handing out a non-string.
 */
export function findLabelById(settings, id) {
    const label = settings.labels[String(id)];
    if (typeof label === 'string')
        return label;
    if (label !== null && typeof label === 'object') {
        const text = label.label;
        return typeof text === 'string' ? text : undefined;
    }
    return undefined;
}
/** First label whose text matches, exact before case-insensitive. */
export function findLabelIdByText(settings, text) {
    // Through `findLabelById`, so an un-normalised array form cannot throw on `.toLowerCase()`.
    const entries = labelIds(settings).map((id) => [id, findLabelById(settings, id) ?? '']);
    const exact = entries.find(([, label]) => label === text);
    if (exact !== undefined)
        return exact[0];
    const lower = text.toLowerCase();
    const loose = entries.find(([, label]) => label.toLowerCase() === lower);
    return loose === undefined ? undefined : loose[0];
}
/** Display position of a label id; falls back to the id when no position is recorded. */
export function labelPosition(settings, id) {
    return settings.labels_positions_v2?.[String(id)] ?? id;
}
/** Label ids in `labels_positions_v2` order — the only ordering the UI may use `[php§4.3]`. */
export function orderedLabelIds(settings) {
    return labelIds(settings).sort((a, b) => labelPosition(settings, a) - labelPosition(settings, b));
}
export function isDoneLabel(settings, id) {
    return (settings.done_colors ?? []).includes(id);
}
export function labelColorIndex(settings, id) {
    return settings.color_mapping?.[String(id)] ?? id;
}
export function labelStyle(settings, id) {
    return settings.labels_colors?.[String(id)];
}
/**
 * label **text** → its live hex, in `labels_positions_v2` order (P-267).
 *
 * The one lookup a consumer that stored the label's *text* rather than its id can do — which is
 * every read of `task_assignments.status_label`. It lives here, in the status folder, because
 * knowing that a label's colour is `labels_colors[id].color` is status vocabulary and reading
 * that record anywhere else would be the `if (type === 'status')` special case CLAUDE.md rule 6
 * forbids.
 *
 * Two deliberate choices:
 *   - **display order wins a duplicate.** monday lets two labels carry the same text; the first
 *     in `labels_positions_v2` is the one the picker shows first, so it is the one that answers.
 *   - a label with no `labels_colors` entry is **absent**, not grey. The palette fallback for a
 *     colourless label is a rendering decision and belongs to whoever paints the pill.
 */
export function labelColorsByText(settings) {
    const out = new Map();
    for (const id of orderedLabelIds(settings)) {
        const text = findLabelById(settings, id);
        if (text === undefined || text === '' || out.has(text))
            continue;
        const color = labelStyle(settings, id)?.color;
        if (typeof color === 'string' && color !== '')
            out.set(text, color);
    }
    return out;
}
/** The next label id the settings dialog allocates: `max(id) + 1`, never a reused id `[php§4.2]`. */
export function nextLabelId(settings) {
    const ids = labelIds(settings);
    return ids.length === 0 ? 0 : Math.max(...ids) + 1;
}
/* ── coercion ─────────────────────────────────────────────────────────────── */
function resolveId(raw, settings, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    let id;
    let rest = {};
    if (typeof raw === 'number') {
        id = raw;
    }
    else if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed === '')
            return null;
        if (/^\d+$/.test(trimmed)) {
            // Verbatim doc warning: *"If a label is numeric (e.g. "2023"), always update by index, not
            // label text. The API will interpret the string as an index number."* `[09§A.7 status]`
            id = Number(trimmed);
        }
        else {
            id = findLabelIdByText(settings, trimmed);
            if (id === undefined)
                throw new ColumnValueError('status', `unknown label "${trimmed}"`);
        }
    }
    else if (typeof raw === 'object') {
        const record = raw;
        if (keepVolatile) {
            rest = { ...record };
            delete rest.index;
            delete rest.label;
        }
        else {
            if (typeof record.post_id === 'string' || record.post_id === null)
                rest.post_id = record.post_id;
            if (record.invalid === true)
                rest.invalid = true;
            if (typeof record.textual_value === 'string')
                rest.textual_value = record.textual_value;
        }
        if (typeof record.index === 'number') {
            id = record.index;
        }
        else if (typeof record.index === 'string' && /^\d+$/.test(record.index)) {
            id = Number(record.index);
        }
        else if (typeof record.label === 'string') {
            if (record.label.trim() === '')
                return null;
            id = findLabelIdByText(settings, record.label);
            if (id === undefined)
                throw new ColumnValueError('status', `unknown label "${record.label}"`);
        }
        if (id === undefined) {
            /**
             * A **cleared** status that kept the update post which recorded the clearing:
             * `{"post_id":…,"changed_at":…}` with neither `index` nor `label`. monday stores 9 such
             * cells (T-X2 hook 8, `specs/M12-X2-importer.md` X2-9). On import that is emptiness, not
             * a malformed value — only `post_id` is lost. A **write** still refuses it, because a
             * caller who sends no label meant something.
             */
            if (keepVolatile)
                return null;
            throw new ColumnValueError('status', 'expected an "index" or "label" key');
        }
    }
    else {
        throw new ColumnValueError('status', `expected an object, received ${typeof raw}`);
    }
    if (!Number.isInteger(id))
        throw new ColumnValueError('status', 'index must be an integer');
    // An id absent from `labels` is rejected on write — monday returns
    // *"invalid value - label does not exist in column"* (recorded, corpus 0203). On **import** a
    // broken reference is kept verbatim instead: monday itself stores 21 such cells `[cvs§2].`
    if (!keepVolatile && Object.keys(settings.labels).length > 0 && findLabelById(settings, id) === undefined) {
        throw new ColumnValueError('status', `label id ${id} does not exist in column (use create_labels_if_missing at the service layer)`);
    }
    return { index: id, ...rest };
}
/* ── settings serialisations ──────────────────────────────────────────────── */
function fromArrayForm(labels) {
    const texts = {};
    const positions = {};
    const colors = {};
    const colorMapping = {};
    const done = [];
    const deactivated = [];
    for (const entry of labels) {
        if (typeof entry !== 'object' || entry === null)
            continue;
        const label = entry;
        const id = Number(label.id ?? label.index);
        if (!Number.isInteger(id))
            continue;
        const key = String(id);
        texts[key] = typeof label.label === 'string' ? label.label : '';
        positions[key] = typeof label.index === 'number' ? label.index : id;
        if (typeof label.hex === 'string')
            colors[key] = { color: label.hex, border: label.hex };
        if (typeof label.color === 'number' && label.color !== id)
            colorMapping[key] = label.color;
        if (label.is_done === true)
            done.push(id);
        if (label.is_deactivated === true)
            deactivated.push(id);
    }
    return {
        labels: texts,
        labels_positions_v2: positions,
        labels_colors: colors,
        done_colors: done,
        color_mapping: colorMapping,
        deactivated_labels: deactivated,
    };
}
export const statusColumn = defineColumnType({
    type: 'status',
    title: 'Status',
    description: 'A coloured label from a fixed list, with a "done" flag per label.',
    apiFragmentName: 'StatusValue',
    legacyTypeName: 'color',
    idPrefix: 'status',
    valueSchema: statusValueSchema,
    settingsSchema: statusSettingsSchema,
    defaultSettings: DEFAULT_STATUS_SETTINGS,
    settingsStrSchema: z.looseObject({}),
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    filterConditions: statusFilterConditions,
    filterOperators: operatorsOf(statusFilterConditions),
    aggregations: ['battery', 'count', 'count_empty', 'none'],
    emptyText: '',
    parseValue(raw, settings) {
        return resolveId(raw, settings, false);
    },
    parseSimpleValue(raw, settings) {
        return resolveId(raw, settings, false);
    },
    toText(value, settings) {
        if (value === null)
            return '';
        return findLabelById(settings, value.index) ?? '';
    },
    toSortKeys(value, settings) {
        if (value === null)
            return { text: null, number: null, date: null };
        return {
            text: textShadow(findLabelById(settings, value.index)),
            // Sorting follows the label order the user arranged, not the id (**ASSUMED**, Q59).
            number: labelPosition(settings, value.index),
            date: null,
        };
    },
    isEqual(a, b) {
        // A re-pick of the same label is a no-op even though `changed_at` moved (M2 §5.2).
        return (a?.index ?? null) === (b?.index ?? null);
    },
    toActivityValue(value) {
        // `[ba§2]`: `{"post_id": null, "index": 5}`.
        return value === null ? null : { post_id: value.post_id ?? null, index: value.index };
    },
    fromMondayValue(raw, settings) {
        return resolveId(raw, settings, true);
    },
    toMondayValue(value) {
        // The canonical value **is** monday's value; extra keys (`invalid`, `textual_value`) survive.
        return value === null ? null : { ...value };
    },
    toSimpleValue(value) {
        return value === null ? '' : String(value.index);
    },
    fromMondaySettings(raw) {
        const record = (raw ?? {});
        // The array form (`Column.settings`) — accepted so an import can use either serialisation.
        if (Array.isArray(record.labels)) {
            const derived = fromArrayForm(record.labels);
            if (typeof record.hide_footer === 'boolean')
                derived.hide_footer = record.hide_footer;
            return statusSettingsSchema.parse(derived);
        }
        return statusSettingsSchema.parse({ labels: {}, ...record });
    },
    toMondaySettings(settings) {
        const labels = labelIds(settings)
            .sort((a, b) => a - b)
            .map((id) => {
            const style = labelStyle(settings, id);
            const label = {
                id,
                color: labelColorIndex(settings, id),
                label: findLabelById(settings, id) ?? '',
                index: labelPosition(settings, id),
                is_done: isDoneLabel(settings, id),
                is_deactivated: (settings.deactivated_labels ?? []).includes(id),
            };
            if (style !== undefined)
                label.hex = style.color;
            return label;
        });
        return { labels };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value, settings) {
        if (value === null)
            return {};
        const style = labelStyle(settings, value.index);
        return {
            index: value.index,
            label: findLabelById(settings, value.index) ?? null,
            is_done: isDoneLabel(settings, value.index),
            update_id: value.post_id ?? null,
            label_style: style === undefined ? null : { color: style.color, border: style.border },
            updated_at: value.changed_at ?? null,
        };
    },
    /**
     * Recorded: `{"label": {"index": 2, "text": "Stuck", "style": {"color": "#df2f4a",
     * "border": "#ce3048", "var_name": "red-shadow"}, "is_done": false}, "post_id": null}`
     * `[whk§7,§8]`.
     *
     * None of the label text, its colours or `is_done` is in the stored value — they come out of
     * `settings_str.labels`, `labels_colors` and `done_colors` `[php§4]`, which is why the
     * webhook body carries information `ColumnValue.value` does not. `var_name` is the
     * **hyphenated** vocabulary, stored verbatim, never derived `[php§4.6]`.
     */
    toWebhookValue(value, settings) {
        if (value === null)
            return null;
        const style = labelStyle(settings, value.index);
        return {
            label: {
                index: value.index,
                text: findLabelById(settings, value.index) ?? '',
                style: {
                    color: style?.color ?? null,
                    border: style?.border ?? null,
                    var_name: style?.var_name ?? null,
                },
                is_done: isDoneLabel(settings, value.index),
            },
            post_id: value.post_id ?? null,
        };
    },
    /** `any_of` compares the label **id**, never the text `[cvs§4.7]`. */
    bucketsOf(value, settings) {
        const id = value?.index;
        if (id === undefined || id === null)
            return [];
        // `labels_colors` is the live hex `[php§4.6]`; the *palette* lookup for a label with no
        // stored colour belongs to `packages/ui`, so a bucket with none simply carries no colour.
        return [
            { key: id, label: findLabelById(settings, id) ?? `#${String(id)}`, color: labelStyle(settings, id)?.color },
        ];
    },
    /**
     * A kanban drop (`specs/M4-V2-kanban.md` §6.1). The label id is the bucket key; `post_id`
     * and `changed_at` belong to the update that recorded the previous change and are dropped,
     * exactly as a fresh write through the grid drops them.
     */
    valueForBucket(bucketKey, settings) {
        if (!/^\d+$/.test(bucketKey))
            return null;
        const id = Number(bucketKey);
        return findLabelById(settings, id) === undefined ? null : { index: id };
    },
    /** Every label, in `labels_positions_v2` order — the order the quick-filter pane uses. */
    bucketVocabulary(settings) {
        return orderedLabelIds(settings).map((id) => ({
            key: id,
            label: findLabelById(settings, id) ?? `#${String(id)}`,
            color: labelStyle(settings, id)?.color,
        }));
    },
    /**
     * `text is` / `text is not` (`compare_attribute: "text"`) compare the **label text**, not
     * the stored index `[sdl]` — which is exactly the text shadow `toSortKeys` writes. Without
     * the half declared, both rows fell into the `any_of` branch below and compared a label
     * *name* against a label *id*, so they matched nothing (round 5z).
     */
    compareAttributes: {
        text: {
            shadow: 'text',
            keys: (value, settings) => ({
                text: value === null ? null : textShadow(findLabelById(settings, value.index)),
                number: null,
                date: null,
            }),
        },
    },
    filterSql(ctx) {
        const cell = ctx.refs?.cell ?? 'cv';
        switch (ctx.operator) {
            case 'is_empty':
                return sqlFragment(`${cell}.item_id IS NULL`);
            case 'is_not_empty':
                return sqlFragment(`${cell}.item_id IS NOT NULL`);
            case 'any_of':
                // Compare values are label **ids** `[cvs§4.7]`, and the number shadow holds the display
                // position, so the id is read back out of the stored value.
                return sqlFragment(`(${cell}.value->>'index')::numeric = ANY(?::numeric[])`, [ctx.compareValues]);
            case 'not_any_of':
                return sqlFragment(`(${cell}.item_id IS NULL OR NOT ((${cell}.value->>'index')::numeric = ANY(?::numeric[])))`, [ctx.compareValues]);
            default:
                return null;
        }
    },
    summarySql(ctx) {
        if (ctx.aggregation === 'battery') {
            const cell = ctx.refs?.cell ?? 'cv';
            // The definition maps each id to its label, colour and `is_done`; the renderer draws the
            // segmented bar. Counting by id (not position) keeps deleted labels visible.
            const fragment = {
                text: `(${cell}.value->>'index')::numeric AS label_id, count(*)`,
                params: [],
                shape: 'buckets',
            };
            return fragment;
        }
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map